/* index.js — /signature-creator: лист, три способа сделать подпись, выдача.
 *
 * Режимы:
 *   ideas — подпись по имени и фамилии (ideas.js): компоновки и росчерки,
 *           написанные пером по средним линиям рукописных шрифтов;
 *   draw  — от руки (ink.js): мышь, палец, стилус с настоящим нажимом;
 *   photo — с фото, скана или страницы PDF (photo.js).
 *
 * ⚠️ Файл отдаётся СИНХРОННО в жесте: картинка готовится заранее (prepare()),
 * к нажатию «Скачать» она уже лежит. Между нажатием и __downloadFile /
 * navigator.share / clipboard.write нет ни одного await: Safari и Android
 * WebView иначе теряют «жест пользователя» и файл не отдают.
 *
 * ⚠️ Лимит дня тратится ТОЛЬКО выданным файлом: единица — подпись (ключ по её
 * рисунку), тот же рисунок в другом формате или размере бесплатен; превью,
 * «Обвести рукой», передача в /sign-pdf лимит не трогают. __downloadFile
 * отвечает обещанием с вердиктом — единица списывается по `=== true`.
 *
 * ⚠️ Подпись — чувствительные данные. Рисунок и имя живут только в этой
 * вкладке (sessionStorage): переживают случайное обновление, но не остаются
 * в браузере после закрытия вкладки — компьютер может быть общим.
 */

import { createPaper } from './paper.js';
import { createInk } from './ink.js';
import { loadFreehand, buildTimeline, frameAt } from './pen.js';
import * as X from './export.js';

const CFG = window.SG_CFG || {};
const T = CFG.t || {};
const SLUG = CFG.slug || 'signature-creator';
const $ = function (id) { return document.getElementById(id); };

function t(k, r) {
    let s = T[k] != null ? String(T[k]) : k;
    if (r) Object.keys(r).forEach(function (n) { s = s.split(':' + n).join(String(r[n])); });
    return s;
}
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (e) {} }
function funnel(stage, data) { try { if (window.__aeFunnel) window.__aeFunnel(stage, data || {}); } catch (e) {} }
function stage(name) { try { if (window.__stage) window.__stage(name); } catch (e) {} }
function report(e, where) { try { if (window.__handleError) window.__handleError(e, 'signature-creator:' + where); } catch (_) {} }
function DK() { return window.DeviceKit || null; }
/* Премиум — по рантайм-правде (__isProNow), а не по разметке: гостевая копия
   страницы из кэша приезжает и вошедшему. Где план не продаётся (__PRO_HERE ===
   false), платные двери открыты: стены без предложения не бывает. */
function isPro() {
    try { if (window.__PRO_HERE === false) return true; } catch (e) {}
    try { if (window.__isProNow && window.__isProNow(false)) return true; } catch (e) {}
    try { const dk = DK(); if (dk && dk.isPro()) return true; } catch (e) {}
    return false;
}
function settings() { return window.__toolSettings || null; }
function load(k, fb) { try { const s = settings(); return s ? s.load(SLUG, k, fb) : fb; } catch (e) { return fb; } }
function save(k, v) { try { const s = settings(); if (s) s.save(SLUG, k, v); } catch (e) {} }
function sessLoad(k) { try { const v = sessionStorage.getItem('sg_' + k); return v ? JSON.parse(v) : null; } catch (e) { return null; } }
function sessSave(k, v) { try { if (v == null) sessionStorage.removeItem('sg_' + k); else sessionStorage.setItem('sg_' + k, JSON.stringify(v)); } catch (e) {} }
function esc(s) { return String(s).replace(/[<>&"]/g, function (c) { return { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]; }); }

const FMT = {
    'png':       { ext: 'png', short: 'PNG' },
    'png-white': { ext: 'png', short: 'PNG' },
    'jpg':       { ext: 'jpg', short: 'JPG' },
    'svg':       { ext: 'svg', short: 'SVG' },
    'pdf':       { ext: 'pdf', short: 'PDF' },
    'anim':      { ext: 'gif', short: 'GIF', pro: true },
};
const DOC_MM = 150;        // сколько миллиметров бумаги показывает лист в режиме «Документ»

const root = $('sg');
if (root) boot();

function boot() {
    const S = {
        mode: 'ideas',
        ink: load('ink', '#1f3d99'),
        inkOrig: false,
        thick: +load('thick', 5) || 5,
        pen: load('pen', 'ball'),
        smooth: +load('smooth', 50),
        guide: load('guide', true) !== false,
        fmt: load('fmt', 'png'),
        mm: +load('mm', 50) || 50,
        bg: 'paper',
        ideas: { first: '', last: '', style: load('style', 'all'), page: 0, list: [], sel: -1, token: 0, sample: true },
        photo: null,
        ghost: null,
        ready: null,
        share: null,
        prepTimer: 0,
        replay: null,
    };
    if (!FMT[S.fmt] || (FMT[S.fmt].pro && !isPro())) S.fmt = 'png';
    if (['ball', 'gel', 'marker', 'calligraphy'].indexOf(S.pen) < 0) S.pen = 'ball';

    const paperEl = $('sgPaper');
    const canvas = $('sgCanvas');
    const paper = createPaper(paperEl, canvas, { onResize: function () { renderArt(); } });
    paper.setGuide(S.guide);

    let ink = null;
    let Ideas = null, IdeasP = null, Photo = null, PhotoP = null, AnimP = null;
    function loadIdeas() {
        if (Ideas) return Promise.resolve(Ideas);
        if (!IdeasP) IdeasP = import('./ideas.js').then(function (m) { Ideas = m; return m; }).catch(function (e) { IdeasP = null; throw e; });
        return IdeasP;
    }
    function loadPhoto() {
        if (Photo) return Promise.resolve(Photo);
        if (!PhotoP) PhotoP = import('./photo.js').then(function (m) { Photo = m; return m; }).catch(function (e) { PhotoP = null; throw e; });
        return PhotoP;
    }
    function loadAnim() {
        if (!AnimP) AnimP = import('./anim.js').catch(function (e) { AnimP = null; throw e; });
        return AnimP;
    }

    /* ── текущая подпись ─────────────────────────────────────────────── */
    function currentIdea() { return S.mode === 'ideas' ? S.ideas.list[S.ideas.sel] || null : null; }
    function currentArt() {
        if (S.mode === 'draw') return ink ? ink.art() : null;
        if (S.mode === 'ideas') {
            const it = currentIdea();
            return it && it.art ? recolor(it.art, S.ink) : null;
        }
        if (S.mode === 'photo') {
            if (!S.photo || !S.photo.art) return null;
            return S.inkOrig ? S.photo.art : recolor(S.photo.art, S.ink);
        }
        return null;
    }
    function recolor(art, c) {
        return {
            paths: art.paths.map(function (p) { return { d: p.d, fill: c, sw: p.sw }; }),
            bbox: art.bbox, fit: art.fit !== false,
            raster: art.raster ? Object.assign({}, art.raster, { tint: c }) : null,
        };
    }
    /* Анимация текущей подписи: штрихи с временем (идеи — от пера, рисунок —
       от руки). У фото и «Каллиграфии» анимации в файл нет. */
    function currentAnim() {
        if (S.mode === 'ideas') {
            const it = currentIdea();
            return it && it.anim && it.anim.strokes.length && !it.anim.outline ? it.anim : null;
        }
        if (S.mode === 'draw' && ink && !ink.isEmpty()) return ink.anim();
        return null;
    }

    /* Как рисунок ложится на лист: вписать в поле или показать в настоящем
       размере на бланке. */
    function placed(art) {
        if (!art) return null;
        if (S.bg !== 'doc') return art.fit === false ? art : Object.assign({}, art, { fit: true });
        const k = (S.mm / DOC_MM) * paper.W / art.bbox.w;
        const cx = paper.W * 0.2, cy = paper.H * 0.1;
        const bx = art.bbox.x + art.bbox.w / 2, by = art.bbox.y + art.bbox.h / 2;
        return Object.assign({}, art, { fit: false, __m: { k: k, dx: cx - bx * k, dy: cy - by * k } });
    }

    function renderArt() {
        if (S.replay) return;
        /* Рамка выделения на фото/PDF: лист показывает весь снимок, выдачи нет. */
        if (S.mode === 'photo' && S.photo && S.photo.cropping) {
            paper.setArt(S.photo.art);
            refreshChrome(null);
            $('sgEmpty').hidden = true;
            return;
        }
        const art = currentArt();
        paper.setArt(placed(art));
        refreshChrome(art);
        schedulePrepare();
    }

    /* ── док и доступность действий ──────────────────────────────────── */
    const dlBtn = $('sgDownload'), copyBtn = $('sgCopy'), shareBtn = $('sgShare'), signBtn = $('sgSignPdf');
    const menuDl = $('sgMenuDl');
    function refreshChrome(art) {
        const has = !!(art && ((art.paths && art.paths.length) || art.raster));
        dlBtn.disabled = !has;
        menuDl.disabled = !has;
        copyBtn.disabled = !has || !X.canCopyImage();
        shareBtn.disabled = !has;
        if (signBtn) { signBtn.setAttribute('aria-disabled', has ? 'false' : 'true'); signBtn.classList.toggle('is-off', !has); }
        const emptyEl = $('sgEmpty');
        emptyEl.hidden = has || (S.mode === 'photo' && !!S.photo);
        if (S.mode === 'ideas' && S.ideas.sample && has) emptyEl.hidden = false;
        emptyEl.classList.toggle('is-sample', S.mode === 'ideas' && S.ideas.sample && has);
        const sz = $('sgSize');
        if (has) {
            sz.hidden = false;
            sz.textContent = t('size_badge', { w: S.mm, h: Math.max(1, Math.round(S.mm * art.bbox.h / art.bbox.w)) });
        } else sz.hidden = true;
        $('sgReplay').hidden = !(has && currentAnim());
        if (S.mode === 'draw' && ink) {
            $('sgUndo').disabled = !ink.canUndo();
            $('sgRedo').disabled = !ink.canRedo();
            $('sgClear').disabled = ink.isEmpty();
        }
    }

    /* ── режимы ──────────────────────────────────────────────────────── */
    const tabs = Array.prototype.slice.call(document.querySelectorAll('.sg-modes [data-mode]'));
    function setMode(m, silent) {
        if (m !== 'ideas' && m !== 'draw' && m !== 'photo') m = 'ideas';
        stopReplay();
        S.mode = m;
        root.setAttribute('data-mode', m);
        tabs.forEach(function (b) {
            const on = b.getAttribute('data-mode') === m;
            b.setAttribute('aria-selected', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
        });
        Array.prototype.forEach.call(root.querySelectorAll('.sg-pane'), function (p) {
            p.hidden = p.getAttribute('data-pane') !== m;
        });
        $('sgDrawTools').hidden = m !== 'draw';
        $('sgFull').hidden = m !== 'draw';
        $('sgThickBox').hidden = m === 'photo';
        $('sgInks').querySelector('[data-ink=orig]').hidden = m !== 'photo';
        const cal = document.querySelector('#sgPens [data-pen=calligraphy]');
        if (cal) cal.hidden = m !== 'ideas';
        if (m === 'draw' && S.pen === 'calligraphy') setPen('ball', true);
        markPen();
        paper.setGuide(m === 'draw' ? S.guide : false);
        paper.setGhost(m === 'draw' && S.ghost ? S.ghost.art : null, 0.22);
        if (ink) ink.enable(m === 'draw');
        setEmpty();
        if (m === 'draw' && !ink) startInk();
        if (m === 'draw') renderTraceGrid();
        if (m === 'ideas') ensureIdeas();
        if (m === 'photo') loadPhoto().catch(function () {});
        renderArt();
        if (!silent) { save('mode', m); funnel('mode', { mode: m }); }
        requestAnimationFrame(function () { paper.remeasure(); });
    }
    function setEmpty() {
        const big = $('sgEmptyBig'), txt = $('sgEmptyT');
        if (S.mode === 'draw') { big.textContent = t('empty_big'); txt.textContent = t('empty_draw'); big.hidden = false; }
        else if (S.mode === 'photo') { big.hidden = true; txt.textContent = S.photoBusy ? t('photo_opening') : t('empty_photo'); }
        else { big.hidden = true; txt.textContent = S.ideas.sample && S.ideas.list.length ? t('sample_hint') : t('empty_ideas'); }
    }
    tabs.forEach(function (b, i) {
        b.addEventListener('click', function () {
            const m = b.getAttribute('data-mode');
            if (m === S.mode) return;
            if (window.__viewTransition) window.__viewTransition(function () { setMode(m); });
            else setMode(m);
        });
        b.addEventListener('keydown', function (e) {
            const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!dir) return;
            e.preventDefault();
            const rtl = (document.documentElement.getAttribute('dir') || '') === 'rtl';
            const n = tabs[(i + (rtl ? -dir : dir) + tabs.length) % tabs.length];
            n.focus(); setMode(n.getAttribute('data-mode'));
        });
    });

    /* ── от руки ─────────────────────────────────────────────────────── */
    function startInk() {
        loadFreehand().then(function () {
            ink = createInk(paper, {
                pen: S.pen === 'calligraphy' ? 'ball' : S.pen, thickness: S.thick, streamline: streamOf(S.smooth), color: S.ink,
                onStart: function () {
                    stopReplay();
                    if (S.bg === 'doc') setBg('paper');
                    $('sgEmpty').hidden = true;
                    stage('drawing');
                },
                onChange: function (why) {
                    renderArt();
                    sessSave('strokes', ink.snapshot());
                    if (why === 'stroke') funnel('stroke', { n: ink.count() });
                },
            });
            const kept = sessLoad('strokes');
            if (kept && kept.length) ink.restore(kept);
            ink.enable(S.mode === 'draw');
            renderArt();
        }).catch(function (e) { toast(t('err_engine'), 'error'); report(e, 'freehand'); });
    }
    function streamOf(v) { return 0.15 + (+v) * 0.006; }

    $('sgUndo').addEventListener('click', function () { if (ink) ink.undo(); });
    $('sgRedo').addEventListener('click', function () { if (ink) ink.redo(); });
    $('sgClear').addEventListener('click', function () {
        if (!ink || !ink.clear()) return;
        sessSave('strokes', null);
        toast(t('cleared'), 'info');
    });
    document.addEventListener('keydown', function (e) {
        if (S.mode !== 'draw' || !ink) return;
        const tg = e.target;
        if (tg && (tg.tagName === 'INPUT' || tg.tagName === 'TEXTAREA' || tg.isContentEditable)) return;
        const mod = e.ctrlKey || e.metaKey;
        const z = e.code === 'KeyZ';
        if (mod && z && !e.shiftKey) { e.preventDefault(); ink.undo(); }
        else if (mod && ((z && e.shiftKey) || e.code === 'KeyY')) { e.preventDefault(); ink.redo(); }
    });

    const smooth = $('sgSmooth');
    smooth.value = S.smooth;
    $('sgSmoothV').textContent = S.smooth;
    smooth.addEventListener('input', function () {
        S.smooth = +smooth.value;
        $('sgSmoothV').textContent = S.smooth;
        save('smooth', S.smooth);
        if (ink) ink.setStreamline(streamOf(S.smooth));
    });
    const guide = $('sgGuide');
    guide.checked = S.guide;
    guide.addEventListener('change', function () {
        S.guide = guide.checked;
        save('guide', S.guide);
        paper.setGuide(S.mode === 'draw' && S.guide);
    });

    /* ── повтор «как пишется» ────────────────────────────────────────── */
    function stopReplay() {
        if (!S.replay) return;
        cancelAnimationFrame(S.replay.raf);
        S.replay = null;
        $('sgReplay').setAttribute('aria-pressed', 'false');
    }
    $('sgReplay').addEventListener('click', function () {
        if (S.replay) { stopReplay(); renderArt(); return; }
        const anim = currentAnim();
        if (!anim) return;
        const tl = buildTimeline(anim.strokes, { leadMs: 150, holdMs: 0 });
        const base = placed(currentArt());
        const fill = S.ink;
        const t0 = performance.now();
        const btn = $('sgReplay');
        btn.setAttribute('aria-pressed', 'true');
        S.replay = { raf: 0 };
        function frame(now) {
            if (!S.replay) return;
            const ms = now - t0;
            const paths = frameAt(tl, ms).map(function (p) { return { d: p.d, fill: fill }; });
            paper.setArt(Object.assign({}, base, { paths: paths }));
            paper.renderNow();
            if (ms < tl.total) S.replay.raf = requestAnimationFrame(frame);
            else { S.replay = null; btn.setAttribute('aria-pressed', 'false'); renderArt(); }
        }
        S.replay.raf = requestAnimationFrame(frame);
        funnel('replay', { mode: S.mode, ms: Math.round(tl.total) });
    });

    /* ── большой лист (телефон, планшет) ─────────────────────────────── */
    const big = $('sgBig'), bigBody = $('sgBigBody');
    let bigHome = null;
    function openBig() {
        if (!big || typeof big.showModal !== 'function') { toast(t('fullscreen_na'), 'info'); return; }
        bigHome = paperEl.parentNode;
        bigBody.appendChild(paperEl);
        try { big.showModal(); } catch (e) { bigHome.appendChild(paperEl); bigHome = null; return; }
        /* Android и iPad: настоящий весь экран и горизонтальный лист; на
           iPhone полноэкранного режима для страницы нет — диалог во весь
           экран и подсказка повернуть телефон. */
        const fsEl = $('sgBigIn');
        if (window.__safeFullscreen && fsEl && (fsEl.requestFullscreen || fsEl.webkitRequestFullscreen)) {
            window.__safeFullscreen(fsEl, {}).then(function (ok) {
                if (ok) { try { if (screen.orientation && screen.orientation.lock) screen.orientation.lock('landscape').catch(function () {}); } catch (e) {} }
            });
        }
        requestAnimationFrame(function () { paper.remeasure(); });
        funnel('big_pad', { open: 1 });
    }
    if (big) {
        big.addEventListener('close', function () {
            if (bigHome) { bigHome.appendChild(paperEl); bigHome = null; }
            try { if (screen.orientation && screen.orientation.unlock) screen.orientation.unlock(); } catch (e) {}
            try { if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(function () {}); else if (document.webkitFullscreenElement && document.webkitExitFullscreen) document.webkitExitFullscreen(); } catch (e) {}
            requestAnimationFrame(function () { paper.remeasure(); renderArt(); });
        });
        $('sgBigDone').addEventListener('click', function () { if (big.open) { try { big.close(); } catch (e) {} } });
    }
    $('sgFull').addEventListener('click', openBig);

    /* ── идеи по имени ───────────────────────────────────────────────── */
    const firstIn = $('sgFirst'), lastIn = $('sgLast');
    const keptNames = sessLoad('names');
    if (keptNames) {
        firstIn.value = keptNames.first || ''; lastIn.value = keptNames.last || '';
        S.ideas.first = firstIn.value.trim(); S.ideas.last = lastIn.value.trim();
    }
    let nameTimer = 0;
    function onName() {
        clearTimeout(nameTimer);
        nameTimer = setTimeout(function () {
            const f = firstIn.value.trim(), l = lastIn.value.trim();
            if (f === S.ideas.first && l === S.ideas.last) return;
            S.ideas.first = f; S.ideas.last = l;
            S.ideas.page = 0;
            sessSave('names', { first: firstIn.value, last: lastIn.value });
            regenerate(true);
        }, 240);
    }
    firstIn.addEventListener('input', onName);
    lastIn.addEventListener('input', onName);
    firstIn.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); lastIn.focus(); } });
    lastIn.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); lastIn.blur(); } });

    Array.prototype.forEach.call(document.querySelectorAll('#sgStyles [data-style]'), function (b) {
        b.addEventListener('click', function () {
            S.ideas.style = b.getAttribute('data-style');
            save('style', S.ideas.style);
            markStyle();
            S.ideas.page = 0;
            regenerate(true);
            funnel('style', { style: S.ideas.style });
        });
    });
    function markStyle() {
        Array.prototype.forEach.call(document.querySelectorAll('#sgStyles [data-style]'), function (b) {
            b.setAttribute('aria-pressed', b.getAttribute('data-style') === S.ideas.style ? 'true' : 'false');
        });
    }
    markStyle();

    $('sgMore').addEventListener('click', function () {
        S.ideas.page++;
        regenerate(false);
        funnel('more_ideas', { page: S.ideas.page });
    });

    /* Обвести рукой: вариант ложится бледно под перо в режиме «от руки». */
    $('sgTrace').addEventListener('click', function () {
        const it = currentIdea();
        if (!it || S.ideas.sample) { toast(t('trace_need'), 'info'); return; }
        S.ghost = { art: ghostArt(it.art), i: S.ideas.sel < 6 ? S.ideas.sel : null };
        $('sgGhostRow').hidden = false;
        $('sgGhostTxt').textContent = t('trace_on');
        setMode('draw');
        funnel('trace', { template: it.template, face: it.face });
    });
    $('sgGhostOff').addEventListener('click', function () {
        S.ghost = null;
        $('sgGhostRow').hidden = true;
        paper.setGhost(null);
        markTrace(-1);
    });
    /* Образцы для обводки в режиме «от руки» — варианты с именем человека. */
    const traceGrid = $('sgTraceGrid'), traceEmpty = $('sgTraceEmpty');
    function renderTraceGrid() {
        traceGrid.textContent = '';
        const list = S.ideas.sample ? [] : S.ideas.list.slice(0, 6);
        traceEmpty.hidden = list.length > 0;
        list.forEach(function (it, i) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'sg-idea';
            b.setAttribute('role', 'radio');
            b.setAttribute('aria-checked', 'false');
            b.setAttribute('aria-label', t('idea_n', { n: i + 1 }));
            b.innerHTML = cardSvg(it.art);
            b.addEventListener('click', function () {
                S.ghost = { art: ghostArt(it.art), i: i };
                paper.setGhost(S.ghost.art, 0.22);
                $('sgGhostRow').hidden = false;
                $('sgGhostTxt').textContent = t('trace_on');
                markTrace(i);
                funnel('trace', { template: it.template, face: it.face, from: 'draw' });
            });
            traceGrid.appendChild(b);
        });
        markTrace(S.ghost && S.ghost.i != null ? S.ghost.i : -1);
    }
    function markTrace(i) {
        Array.prototype.forEach.call(traceGrid.children, function (el, j) { el.setAttribute('aria-checked', j === i ? 'true' : 'false'); });
    }
    $('sgTraceGo').addEventListener('click', function () {
        setMode('ideas');
        try { lastIn.focus({ preventScroll: true }); lastIn.scrollIntoView({ block: 'nearest' }); } catch (e) {}
    });
    function ghostArt(art) {
        const box = { w: paper.W * 0.8, h: paper.H * 0.62 };
        const tr = paper.fitTransform(art.bbox, box);
        const k = tr.k, dx = tr.dx, dy = tr.dy;
        return {
            paths: art.paths.map(function (p) { return { d: transformPath(p.d, k, dx, dy), sw: p.sw ? p.sw * k : 0 }; }),
            bbox: { x: art.bbox.x * k + dx, y: art.bbox.y * k + dy, w: art.bbox.w * k, h: art.bbox.h * k },
            fit: false,
        };
    }

    const ideasEl = $('sgIdeas');
    const moreBtn = $('sgMore');
    function ensureIdeas() { if (!S.ideas.list.length) regenerate(true); }
    function regenerate(reset) {
        const tok = ++S.ideas.token;
        const has = !!(S.ideas.first || S.ideas.last);
        const nm = has ? { first: S.ideas.first, last: S.ideas.last } : { first: t('first_ph'), last: t('last_ph') };
        const from = reset ? 0 : S.ideas.list.length;
        if (reset) { S.ideas.list = []; S.ideas.sel = -1; ideasEl.textContent = ''; }
        S.ideas.sample = !has;
        ideasEl.setAttribute('aria-busy', 'true');
        moreBtn.disabled = true;
        stage(has ? 'ideas' : 'ideas_sample');
        loadIdeas().then(function (M) {
            return M.generate({
                first: nm.first, last: nm.last, style: S.ideas.style, page: S.ideas.page,
                count: 12, pen: S.pen, weight: S.thick,
                isCancelled: function () { return tok !== S.ideas.token; },
                onIdea: function (idea) {
                    if (tok !== S.ideas.token) return;
                    S.ideas.list.push(idea);
                    const i = S.ideas.list.length - 1;
                    addCard(i, i - from);
                    if (S.ideas.sel < 0) { S.ideas.sel = 0; markIdeas(); renderArt(); setEmpty(); }
                },
            });
        }).then(function (res) {
            if (tok !== S.ideas.token) return;
            ideasEl.removeAttribute('aria-busy');
            moreBtn.disabled = false;
            if (res.missing && res.missing.length) toast(t('font_missing', { chars: res.missing.join(' ') }), 'warning');
            if (!S.ideas.list.length) { renderArt(); setEmpty(); }
            if (has && reset && S.ideas.list.length) funnel('ideas', { n: S.ideas.list.length, style: S.ideas.style });
        }).catch(function (e) {
            if (tok !== S.ideas.token) return;
            ideasEl.removeAttribute('aria-busy');
            moreBtn.disabled = false;
            toast(t('err_engine'), 'error');
            report(e, 'ideas');
        });
    }
    function addCard(i, order) {
        const it = S.ideas.list[i];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sg-idea is-new';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', i === S.ideas.sel ? 'true' : 'false');
        b.setAttribute('aria-label', t('idea_n', { n: i + 1 }));
        b.tabIndex = (i === S.ideas.sel || (S.ideas.sel < 0 && i === 0)) ? 0 : -1;
        b.style.setProperty('--i', String(Math.max(0, order)));
        b.innerHTML = cardSvg(it.art);
        const n = document.createElement('span');
        n.className = 'sg-idea-n';
        n.textContent = String(i + 1);
        b.appendChild(n);
        b.addEventListener('click', function () { pickIdea(i, true); });
        b.addEventListener('keydown', function (e) { ideaKeys(e, i); });
        ideasEl.appendChild(b);
        setTimeout(function () { b.classList.remove('is-new'); }, 1200);
    }
    function cardSvg(art) {
        const bb = art.bbox;
        const pad = Math.max(bb.h * 0.12, bb.w * 0.06);
        let vw = bb.w + pad * 2, vh = bb.h + pad * 2;
        const ar = 2.35;
        let vx = bb.x - pad, vy = bb.y - pad;
        if (vw / vh < ar) { const nw = vh * ar; vx -= (nw - vw) / 2; vw = nw; }
        else { const nh = vw / ar; vy -= (nh - vh) / 2; vh = nh; }
        let body = '';
        for (let i = 0; i < art.paths.length; i++) {
            const p = art.paths[i];
            body += '<path d="' + p.d + '"' + (p.sw ? ' stroke="currentColor" stroke-width="' + (+p.sw).toFixed(2) + '" stroke-linejoin="round"' : '') + '/>';
        }
        return '<svg viewBox="' + vx.toFixed(1) + ' ' + vy.toFixed(1) + ' ' + vw.toFixed(1) + ' ' + vh.toFixed(1) +
            '" preserveAspectRatio="xMidYMid meet" aria-hidden="true" fill="currentColor" style="color:' + esc(S.ink) + '">' + body + '</svg>';
    }
    function pickIdea(i, user) {
        if (!S.ideas.list[i]) return;
        stopReplay();
        S.ideas.sel = i;
        markIdeas();
        renderArt();
        if (S.ideas.sample && user) {
            toast(t('need_name'), 'info');
            try { lastIn.focus({ preventScroll: true }); } catch (e) { lastIn.focus(); }
            return;
        }
        if (user) funnel('idea_pick', { i: i, template: S.ideas.list[i].template, face: S.ideas.list[i].face });
    }
    function markIdeas() {
        Array.prototype.forEach.call(ideasEl.children, function (el, j) {
            el.setAttribute('aria-checked', j === S.ideas.sel ? 'true' : 'false');
            el.tabIndex = j === S.ideas.sel ? 0 : -1;
        });
    }
    function ideaKeys(e, i) {
        const wide = getComputedStyle(ideasEl).gridAutoFlow.indexOf('row') === 0;
        const cols = wide ? 2 : 1;
        const rtl = (document.documentElement.getAttribute('dir') || '') === 'rtl';
        let j = -1;
        if (e.key === 'ArrowRight') j = rtl ? i - 1 : i + 1;
        else if (e.key === 'ArrowLeft') j = rtl ? i + 1 : i - 1;
        else if (e.key === 'ArrowDown') j = i + cols;
        else if (e.key === 'ArrowUp') j = i - cols;
        if (j < 0 || j >= S.ideas.list.length) return;
        e.preventDefault();
        pickIdea(j, true);
        const el = ideasEl.children[j];
        if (el) { el.focus({ preventScroll: true }); el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
    }
    function refreshCards() {
        Array.prototype.forEach.call(ideasEl.querySelectorAll('.sg-idea svg'), function (s) { s.style.color = S.ink; });
    }
    let restyleTok = 0;
    function restyleIdeas() {
        if (!Ideas || !S.ideas.list.length) return;
        const tok = ++restyleTok;
        Ideas.restyle(S.ideas.list, { pen: S.pen, weight: S.thick }).then(function (list) {
            if (tok !== restyleTok || list.length !== S.ideas.list.length) return;
            S.ideas.list = list;
            Array.prototype.forEach.call(ideasEl.children, function (el, j) {
                const n = el.querySelector('.sg-idea-n');
                el.innerHTML = cardSvg(list[j].art);
                if (n) el.appendChild(n);
            });
            renderArt();
        }).catch(function (e) { report(e, 'restyle'); });
    }

    /* ── с фото ──────────────────────────────────────────────────────── */
    const drop = $('sgDrop');
    /* __bindUploaderFile отдаёт сам File (и detail вторым аргументом), запасной
       путь — событие: принимаем оба. */
    function onPhotoEvent(x) {
        const f = x && x.detail ? x.detail.file : x;
        if (f && typeof f.size === 'number') openPhoto(f);
    }
    if (drop) {
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(drop, onPhotoEvent);
        else drop.addEventListener('tool-uploader:file', onPhotoEvent);
        drop.addEventListener('tool-uploader:clear', function () {
            S.photo = null;
            if (Photo && Photo.reset) Photo.reset();
            $('sgPhotoCtl').hidden = true;
            renderArt();
        });
    }
    /* От выбора файла до первого кадра проходят секунды (PDF — загрузка
       pdf.js и рендер страницы): лист говорит, что файл открывается. */
    function photoBusy(on) {
        S.photoBusy = on;
        if (on) paperEl.setAttribute('aria-busy', 'true'); else paperEl.removeAttribute('aria-busy');
        setEmpty();
    }
    function openPhoto(file) {
        stage('photo_load');
        if (!S.photo) photoBusy(true);
        loadPhoto().then(function (M) {
            return M.open(file, {
                paper: paper, paperEl: paperEl, cropEl: $('sgCrop'),
                pageSelect: $('sgPdfPage'), pageRow: $('sgPdfRow'),
                clean: $('sgClean'), cleanOut: $('sgCleanV'), keepColor: $('sgKeepColor'), lines: $('sgLines'),
                cropBtn: $('sgCropBtn'), rotateBtn: $('sgRotate'), t: t, pdfjs: CFG.pdfjs || null,
                onResult: function (res) {
                    if (S.photoBusy) photoBusy(false);
                    S.photo = res;
                    $('sgPhotoCtl').hidden = false;
                    if (res && !res.cropping && res.keepColor != null) { S.inkOrig = !!res.keepColor; markInk(); }
                    renderArt();
                },
            });
        }).then(function () {
            funnel('photo_open', { type: String(file.type || '').slice(0, 24), kb: Math.round(file.size / 1024) });
        }).catch(function (e) {
            if (S.photoBusy) photoBusy(false);
            toast(e && e.userMessage ? e.userMessage : t('err_image'), 'error');
            report(e, 'photo');
        });
    }

    /* ── чернила, перо, толщина ──────────────────────────────────────── */
    const swatches = Array.prototype.slice.call(document.querySelectorAll('#sgInks .sg-sw[data-ink]'));
    function markInk() {
        const custom = !S.inkOrig && !swatches.some(function (b) { return b.getAttribute('data-ink') === S.ink; });
        swatches.forEach(function (b) {
            const v = b.getAttribute('data-ink');
            const on = v === 'orig' ? S.inkOrig : (!S.inkOrig && v === S.ink);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        const cl = root.querySelector('.sg-sw--custom');
        if (cl) cl.setAttribute('aria-checked', custom ? 'true' : 'false');
        root.style.setProperty('--sg-ink', S.ink);
    }
    function setInk(c, fromUser) {
        S.inkOrig = false;
        S.ink = c;
        save('ink', c);
        markInk();
        if (ink) ink.setColor(c);
        if (S.photo && Photo && Photo.setKeepColor) Photo.setKeepColor(false);
        refreshCards();
        renderArt();
        if (fromUser) funnel('ink', { c: c });
    }
    swatches.forEach(function (b) {
        b.addEventListener('click', function () {
            const v = b.getAttribute('data-ink');
            if (v === 'orig') {
                S.inkOrig = true; markInk();
                if (Photo && Photo.setKeepColor) Photo.setKeepColor(true);
                renderArt();
                return;
            }
            setInk(v, true);
        });
    });
    const custom = $('sgInkCustom');
    custom.value = /^#[0-9a-f]{6}$/i.test(S.ink) ? S.ink : '#1f3d99';
    custom.addEventListener('input', function () { setInk(custom.value, false); });
    custom.addEventListener('change', function () { funnel('ink', { c: 'custom' }); });
    /* Пипетка (Chrome/Edge на компьютере): цвет чернил с экрана — например,
       с открытого рядом скана, чтобы подпись совпала с остальными. */
    const eye = $('sgEye');
    if (typeof window.EyeDropper === 'function') {
        eye.hidden = false;
        eye.addEventListener('click', function () {
            try {
                new window.EyeDropper().open().then(function (r) {
                    const hex = r && r.sRGBHex ? r.sRGBHex.slice(0, 7) : '';
                    if (/^#[0-9a-f]{6}$/i.test(hex)) { custom.value = hex; setInk(hex, true); }
                }).catch(function () {});
            } catch (e) {}
        });
    }
    markInk();

    const penBtns = Array.prototype.slice.call(document.querySelectorAll('#sgPens [data-pen]'));
    function markPen() {
        penBtns.forEach(function (b) {
            const on = b.getAttribute('data-pen') === S.pen;
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
    }
    function setPen(p, silent) {
        S.pen = p;
        if (!silent) save('pen', p);
        markPen();
        if (ink && p !== 'calligraphy') ink.setPen(p);
        if (S.mode === 'ideas') restyleIdeas();
    }
    penBtns.forEach(function (b) {
        b.addEventListener('click', function () {
            setPen(b.getAttribute('data-pen'));
            funnel('pen', { pen: S.pen, mode: S.mode });
        });
    });
    markPen();

    const thick = $('sgThick');
    thick.value = S.thick;
    let thickTimer = 0;
    thick.addEventListener('input', function () {
        S.thick = +thick.value;
        save('thick', S.thick);
        if (ink) ink.setThickness(S.thick);
        if (S.mode === 'ideas') {
            clearTimeout(thickTimer);
            thickTimer = setTimeout(restyleIdeas, 90);
        }
    });

    /* ── фон листа ───────────────────────────────────────────────────── */
    const bgBtns = Array.prototype.slice.call(document.querySelectorAll('#sgBg [data-bg]'));
    function setBg(bg) {
        S.bg = bg;
        paperEl.setAttribute('data-bg', bg);
        bgBtns.forEach(function (b) { b.setAttribute('aria-checked', b.getAttribute('data-bg') === bg ? 'true' : 'false'); });
        renderArt();
    }
    bgBtns.forEach(function (b) {
        b.addEventListener('click', function () { setBg(b.getAttribute('data-bg')); funnel('bg', { bg: S.bg }); });
    });

    /* ── формат и размер ─────────────────────────────────────────────── */
    const menu = $('sgMenu');
    function closeMenu() {
        try { if (window.__anchoredPopover) window.__anchoredPopover.close(menu); else if (menu.hidePopover) menu.hidePopover(); } catch (e) {}
    }
    function openMenu() {
        try { if (menu.showPopover && menu.hasAttribute('popover')) menu.showPopover(); else $('sgFmtBtn').click(); } catch (e) {}
    }
    const fmtEls = Array.prototype.slice.call(document.querySelectorAll('#sgFmts [data-fmt]'));
    function markFmt() {
        fmtEls.forEach(function (b) { b.setAttribute('aria-checked', b.getAttribute('data-fmt') === S.fmt ? 'true' : 'false'); });
        const nm = FMT[S.fmt].short + (S.fmt === 'png-white' ? ' ' + t('fmt_white_s') : '');
        $('sgFmtName').textContent = nm;
        $('sgMenuFmt').textContent = nm;
    }
    fmtEls.forEach(function (b) {
        b.addEventListener('click', function () {
            const f = b.getAttribute('data-fmt');
            if (FMT[f].pro && !isPro()) { upsell(f); return; }
            S.fmt = f;
            save('fmt', f);
            markFmt();
            schedulePrepare();
            funnel('format', { fmt: f });
        });
    });
    markFmt();
    const widthEls = Array.prototype.slice.call(document.querySelectorAll('#sgWidths [data-mm]'));
    function markWidth() {
        widthEls.forEach(function (b) {
            const on = +b.getAttribute('data-mm') === S.mm;
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
    }
    widthEls.forEach(function (b) {
        b.addEventListener('click', function () {
            S.mm = +b.getAttribute('data-mm');
            save('mm', S.mm);
            markWidth();
            renderArt();
            funnel('size', { mm: S.mm });
        });
    });
    markWidth();

    /* ── Премиум ─────────────────────────────────────────────────────── */
    function upsell(what) {
        const box = $('sgOffer');
        const dk = DK();
        const ic = function (n) { return dk ? dk.icon(n) : ''; };
        const plan = CFG.planName || '';
        box.hidden = false;
        box.innerHTML = '<div class="dk-offer-t">' + ic('star') + '<span>' + esc(t('pro_title', { plan: plan })) + '</span></div>' +
            '<ul><li>' + esc(t('pro_anim')) + '</li><li>' + esc(t('pro_sheet')) + '</li><li>' + esc(t('pro_pack')) + '</li></ul>' +
            (CFG.proUrl ? '<div class="dk-row"><a class="dk-btn dk-primary dk-sm" href="' + esc(CFG.proUrl) + '">' + esc(t('pro_cta', { plan: plan })) + '</a></div>' : '');
        const a = box.querySelector('a');
        if (a) a.addEventListener('click', function () { funnel('perk_click', { what: what }); });
        funnel('perk_upsell', { what: what });
    }
    function renderPro() {
        const pro = isPro();
        Array.prototype.forEach.call(document.querySelectorAll('#sgMenu [data-pro-tag]'), function (el) { el.hidden = pro; });
        if (pro) $('sgOffer').hidden = true;
    }
    const dk0 = DK();
    if (dk0 && dk0.onTier) dk0.onTier(renderPro); else renderPro();

    /* ── выдача ──────────────────────────────────────────────────────── */
    function baseName() {
        let base = '';
        if (S.mode === 'ideas' && !S.ideas.sample) base = [S.ideas.first, S.ideas.last].filter(Boolean).join(' ');
        base = base ? t('file_base_named', { name: base }) : t('file_base');
        try { if (window.__outputName && window.__outputName.sanitize) base = window.__outputName.sanitize(base) || t('file_base'); } catch (e) {}
        return base;
    }
    function nameFor(fmt, suffix) {
        const ext = FMT[fmt] ? FMT[fmt].ext : fmt;
        const base = baseName() + (fmt === 'png-white' ? '-' + t('file_white') : '') + (suffix ? '-' + suffix : '');
        try { if (window.__outputName && window.__outputName.join) return window.__outputName.join(base, ext); } catch (e) {}
        return base + '.' + ext;
    }
    /* Ключ «той же подписи»: рисунок и цвет. Формат и размер в него НЕ входят —
       повторная выдача того же рисунка в другом виде бесплатна. */
    function designKey() {
        const art = currentArt();
        if (!art) return '';
        let h = S.mode + '|' + (S.inkOrig ? 'orig' : S.ink) + '|' + art.paths.length + '|' + (art.raster ? art.raster.key || 'r' : '') + '|';
        for (let i = 0; i < art.paths.length; i++) {
            const d = art.paths[i].d;
            h += d.length + ':' + d.slice(0, 16) + d.slice(-16) + ';';
        }
        let x = 2166136261 >>> 0;
        for (let i = 0; i < h.length; i++) { x ^= h.charCodeAt(i); x = Math.imul(x, 16777619) >>> 0; }
        return 'sg:' + (x >>> 0).toString(36);
    }
    function build(fmt, art) {
        const o = { widthMm: S.mm, dpi: 600, title: t('file_title'), creator: CFG.brand || '' };
        if (fmt === 'png') return X.toPng(art, o).then(function (r) { return r.blob; });
        if (fmt === 'png-white') return X.toPng(art, Object.assign(o, { background: '#ffffff' })).then(function (r) { return r.blob; });
        if (fmt === 'jpg') return X.toJpg(art, Object.assign(o, { background: '#ffffff' })).then(function (r) { return r.blob; });
        if (fmt === 'svg') return Promise.resolve(new Blob([X.toSvg(art, o)], { type: 'image/svg+xml' }));
        if (fmt === 'pdf') return X.toPdf(art, o).then(function (r) { return r.blob; });
        if (fmt === 'anim') {
            const an = currentAnim();
            if (!an) return Promise.reject(Object.assign(new Error('no_anim'), { userMessage: t('anim_na') }));
            return loadAnim().then(function (A) { return A.gif(an, art, Object.assign(o, { ink: S.ink })); });
        }
        return Promise.reject(new Error('bad_format'));
    }

    function schedulePrepare() {
        clearTimeout(S.prepTimer);
        S.prepTimer = setTimeout(prepare, 260);
    }
    function readyKey(fmt) { return fmt + '|' + S.mm + '|' + designKey(); }
    function prepare() {
        const fmt = S.fmt;
        const art = currentArt();
        if (!art) { S.ready = null; return Promise.resolve(null); }
        prepareShare(art);
        if (FMT[fmt].pro && !isPro()) return Promise.resolve(null);
        const key = readyKey(fmt);
        if (S.ready && S.ready.key === key) return Promise.resolve(S.ready);
        const name = nameFor(fmt);
        return build(fmt, art).then(function (blob) {
            if (readyKey(fmt) !== key) return null;
            S.ready = { key: key, blob: blob, name: name, fmt: fmt };
            return S.ready;
        }).catch(function (e) {
            if (!(e && e.userMessage)) report(e, 'prepare');
            return null;
        });
    }
    /* «Отправить» — PNG на БЕЛОМ: WhatsApp превращает прозрачный фон в чёрный,
       и чёрные чернила на нём пропадают. Файл готов до касания. */
    function prepareShare(art) {
        if (!navigator.share || !navigator.canShare) return;
        const key = 'share|' + S.mm + '|' + designKey();
        if (S.share && S.share.key === key) return;
        X.toPng(art, { widthMm: S.mm, dpi: 300, background: '#ffffff' }).then(function (r) {
            let file = null;
            try { file = new File([r.blob], nameFor('png'), { type: 'image/png' }); } catch (e) { return; }
            if (!X.canShareFile(file)) return;
            S.share = { key: key, file: file };
            shareBtn.hidden = false;
            shareBtn.disabled = false;
        }).catch(function () {});
    }

    function begin() {
        const L = window.__toolLimit;
        if (!L) return { commit: function () {}, abort: function () {} };
        return L.begin(SLUG, [designKey()]);
    }
    function deliver(r) {
        const run = begin();
        if (!run) return;
        let p;
        try { p = window.__downloadFile(r.blob, r.name); } catch (e) { run.abort(); toast(t('err_save'), 'error'); return; }
        Promise.resolve(p).then(function (ok) {
            if (ok === false) { run.abort(); return; }
            run.commit();
            funnel('download', { fmt: r.fmt, mode: S.mode, mm: S.mm, kb: Math.round(r.blob.size / 1024) });
            try { if (window.__aeToolComplete) window.__aeToolComplete({ f: 'download', fmt: r.fmt, mode: S.mode }); } catch (e) {}
        }).catch(function () { run.abort(); });
    }
    function onDownload() {
        if (FMT[S.fmt].pro && !isPro()) { upsell(S.fmt); openMenu(); return; }
        const key = readyKey(S.fmt);
        if (S.ready && S.ready.key === key) { deliver(S.ready); return; }
        /* Не успели подготовить (нажали сразу после изменения): собрать и
           отдать — на компьютере жест для скачивания не нужен. */
        dlBtn.disabled = true; menuDl.disabled = true;
        prepare().then(function (r) {
            dlBtn.disabled = false; menuDl.disabled = false;
            if (r) deliver(r);
            else if (S.fmt === 'anim' && !currentAnim()) toast(t('anim_na'), 'info');
            else toast(t('no_signature'), 'warning');
        });
    }
    dlBtn.addEventListener('click', onDownload);
    menuDl.addEventListener('click', function () { onDownload(); closeMenu(); });

    copyBtn.addEventListener('click', function () {
        const art = currentArt();
        if (!art) return;
        const run = begin();
        if (!run) return;
        const png = X.toPng(art, { widthMm: S.mm, dpi: 300 }).then(function (r) { return r.blob; });
        const svg = art.raster ? null : new Blob([X.toSvg(art, { widthMm: S.mm })], { type: 'image/svg+xml' });
        X.copyImage(png, svg).then(function (types) {
            run.commit();
            toast(t('copied'), 'success');
            funnel('copy', { mode: S.mode, svg: types.indexOf('image/svg+xml') >= 0 ? 1 : 0 });
            try { if (window.__aeToolComplete) window.__aeToolComplete({ f: 'copy', mode: S.mode }); } catch (e) {}
        }).catch(function () { run.abort(); toast(t('no_clipboard'), 'warning'); });
    });

    shareBtn.addEventListener('click', function () {
        const sh = S.share;
        if (!sh || sh.key !== 'share|' + S.mm + '|' + designKey()) { const a = currentArt(); if (a) prepareShare(a); toast(t('share_wait'), 'info'); return; }
        const run = begin();
        if (!run) return;
        navigator.share({ files: [sh.file], title: t('file_title') }).then(function () {
            run.commit();
            funnel('share', { mode: S.mode });
        }).catch(function () { run.abort(); });
    });

    /* «Подписать PDF»: подпись уезжает в /sign-pdf через localStorage на
       10 минут — тот тул забирает её, как только открыт документ, и стирает.
       Лимит не тратится: файл не покидает сайт. */
    if (signBtn) signBtn.addEventListener('click', function (e) {
        const art = currentArt();
        if (!art) { e.preventDefault(); toast(t('no_signature'), 'warning'); return; }
        try {
            const r = X.renderToCanvas(art, { widthMm: S.mm, dpi: 300 });
            localStorage.setItem('tmb.signature.handoff', JSON.stringify({ png: r.canvas.toDataURL('image/png'), ts: Date.now(), mm: S.mm, from: SLUG }));
            funnel('handoff_sign_pdf', { mode: S.mode });
        } catch (err) { /* нет места — /sign-pdf откроется пустым, как раньше */ }
    });

    /* ── Премиум: архив и прописи ────────────────────────────────────── */
    $('sgPack').addEventListener('click', function () {
        if (!isPro()) { upsell('pack'); return; }
        const art = currentArt();
        if (!art) { toast(t('no_signature'), 'warning'); return; }
        const bd = window.__batchDeliver;
        if (!bd) { toast(t('err_save'), 'error'); return; }
        const fmts = ['png', 'png-white', 'jpg', 'svg', 'pdf'];
        Promise.all(fmts.map(function (f) { return build(f, art); })).then(function (blobs) {
            return bd.deliver(fmts.map(function (f, i) { return { name: nameFor(f), blob: blobs[i] }; }), { archiveOnly: true, zipName: baseName() + '.zip', slug: SLUG });
        }).then(function () { funnel('pack', { n: fmts.length }); }).catch(function (e) { report(e, 'pack'); });
    });
    const sheetBtn = $('sgSheetPdf');
    if (sheetBtn) sheetBtn.addEventListener('click', function () {
        if (!isPro()) { upsell('sheet'); return; }
        const art = currentArt();
        if (!art) { toast(t('no_signature'), 'warning'); return; }
        const an = currentAnim();
        loadAnim().then(function (A) {
            return A.practiceSheet(art, an, { ink: S.ink, title: t('sheet_title'), hint: t('sheet_hint'), brand: CFG.brand || '' });
        }).then(function (blob) {
            return window.__downloadFile(blob, nameFor('pdf', t('sheet_file')));
        }).then(function () { funnel('practice_sheet', { mode: S.mode }); }).catch(function (e) { report(e, 'sheet'); toast(t('err_save'), 'error'); });
    });

    /* ── старт ───────────────────────────────────────────────────────── */
    const startMode = load('mode', 'ideas');
    setMode(startMode === 'draw' || startMode === 'photo' ? startMode : 'ideas', true);
    if (S.mode !== 'ideas') loadIdeas().catch(function () {});
    loadFreehand().catch(function () {});
    stage('ready');
}

/* Сдвиг и масштаб пути SVG (абсолютные команды из пар чисел: M L Q T C Z). */
export function transformPath(d, k, dx, dy) {
    let i = 0;
    return d.replace(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi, function (num) {
        const v = +num;
        const out = (i % 2 === 0) ? v * k + dx : v * k + dy;
        i++;
        return (+out.toFixed(2)).toString();
    });
}
